import React, { useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import AuthContext from '../context/AuthContext';

const registerSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, 'Name is too short')
    .max(50, 'Name is too long')
    .required('Name is required'),
  email: Yup.string()
    .email('Invalid email')
    .required('Email is required'),
  password: Yup.string()
    .min(6, 'Password must be at least 6 characters')
    .required('Password is required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password'), null], 'Passwords must match')
    .required('Confirm password is required')
});

const Register = () => {
  const { register } = useContext(AuthContext);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (values) => {
    // Remove confirmPassword before sending to API
    const { confirmPassword, ...userData } = values;
    
    setIsSubmitting(true);
    const success = await register(userData);
    setIsSubmitting(false);
    
    if (success) {
      navigate('/', { replace: true });
    }
  };

  return (
    <>
      <h2 className="text-2xl font-bold text-center mb-6">Create an Account</h2>
      
      <Formik
        initialValues={{ name: '', email: '', password: '', confirmPassword: '' }}
        validationSchema={registerSchema}
        onSubmit={handleSubmit}
      >
        {({ errors, touched }) => (
          <Form className="space-y-4">
            <div>
              <label htmlFor="name" className="form-label">Full Name</label>
              <Field
                id="name"
                name="name"
                type="text"
                className={`form-input ${errors.name && touched.name ? 'border-ui-error' : ''}`}
                placeholder="Enter your full name"
              />
              <ErrorMessage name="name" component="div" className="form-error" />
            </div>
            
            <div>
              <label htmlFor="email" className="form-label">Email Address</label>
              <Field
                id="email"
                name="email"
                type="email"
                className={`form-input ${errors.email && touched.email ? 'border-ui-error' : ''}`}
                placeholder="Enter your email"
              />
              <ErrorMessage name="email" component="div" className="form-error" />
            </div>
            
            <div>
              <label htmlFor="password" className="form-label">Password</label>
              <Field
                id="password"
                name="password"
                type="password"
                className={`form-input ${errors.password && touched.password ? 'border-ui-error' : ''}`}
                placeholder="Create a password"
              />
              <ErrorMessage name="password" component="div" className="form-error" />
            </div>
            
            <div>
              <label htmlFor="confirmPassword" className="form-label">Confirm Password</label>
              <Field
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                className={`form-input ${errors.confirmPassword && touched.confirmPassword ? 'border-ui-error' : ''}`}
                placeholder="Confirm your password"
              />
              <ErrorMessage name="confirmPassword" component="div" className="form-error" />
            </div>
            
            <div className="flex items-center">
              <input
                id="terms"
                name="terms"
                type="checkbox"
                className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
              />
              <label htmlFor="terms" className="ml-2 block text-sm text-gray-700">
                I agree to the{' '}
                <a href="#terms" className="text-secondary hover:text-secondary-dark">
                  Terms and Conditions
                </a>
              </label>
            </div>
            
            <div>
              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full btn btn-primary ${isSubmitting ? 'opacity-75 cursor-not-allowed' : ''}`}
              >
                {isSubmitting ? 'Creating Account...' : 'Create Account'}
              </button>
            </div>
            
            <div className="text-center mt-4">
              <p className="text-sm text-gray-600">
                Already have an account?{' '}
                <Link to="/auth/login" className="text-secondary hover:text-secondary-dark">
                  Sign in
                </Link>
              </p>
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default Register;